/**
 * One submission, exactly as it was sent (Plan D12).
 *
 * **Read-only, and deliberately so.** This page used to carry Approve and Reject
 * buttons, which meant the same decision could be made from two screens. Two
 * surfaces for one decision is how a small team stops being sure which is
 * authoritative — and the volunteer screen has to carry it regardless, because a
 * volunteer entered by staff has no registration behind them at all.
 *
 * So the decision moved, and this became what it always was underneath: the
 * record of what a person submitted (§3.12), preserved even after staff have
 * corrected the volunteer record it created. What the volunteer's phone number
 * *is* lives on their profile; what they *typed* lives here.
 *
 * Reached from the submissions panel on the volunteers screen, from the archive
 * at `/registrations`, and from the volunteer it created.
 */

import { Link, useParams } from 'react-router-dom';
import { ageGroupLabel, OCCUPATION, occupationLabel, splitCodes } from '@parinaam-vms/shared';
import type { RegistrationRequest } from '@parinaam-vms/shared';
import {
  Card,
  DetailList,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from '../../components/common';
import { CRUMB } from '../../constants/breadcrumbs';
import { useRegistration } from '../../hooks/useRegistrations';
import { useReferenceValuesGrouped } from '../../hooks/useReferenceValues';
import type { ReferenceValuesByCategory } from '../../services/api/reference-values';
import { formatDateTime, formatName, humaniseCode, orDash } from '../../utils/format';
import styles from './RegistrationReviewPage.module.css';

export function RegistrationReviewPage() {
  const { id } = useParams<{ id: string }>();

  const query = useRegistration(id);
  const reference = useReferenceValuesGrouped();

  if (query.isPending) return <LoadingState label="Loading the registration…" />;
  if (query.isError) {
    return (
      <>
        <PageHeader
          title="Registration"
          breadcrumbs={[CRUMB.registrations, { label: 'Registration' }]}
        />
        <Card>
          <ErrorState error={query.error} onRetry={() => void query.refetch()} />
        </Card>
      </>
    );
  }

  const submission = query.data as RegistrationRequest;

  return (
    <>
      <PageHeader
        title={formatName(submission)}
        description={`${submission.registration_code} · submitted ${formatDateTime(submission.submitted_at)}`}
        actions={<StatusBadge status={submission.status} />}
        breadcrumbs={[CRUMB.registrations, { label: formatName(submission) }]}
      />

      {/*
        Not a way back — a way across. The breadcrumb handles "up to the archive";
        this points at where everything actually happens for this person: activate,
        reject, edit, delete.
      */}
      {submission.created_volunteer_id && (
        <p className={styles.crossLink}>
          <Link to={`/volunteers/${submission.created_volunteer_id}`}>
            Open the volunteer record →
          </Link>
        </p>
      )}

      <p className={styles.readOnlyNote}>
        Submitted as sent; this cannot be edited.
      </p>

      <Card title="Submitted details">
        <DetailList
          items={[
            { label: 'Full name', value: formatName(submission) },
            { label: 'Preferred name', value: orDash(submission.preferred_name) },
            { label: 'Email', value: orDash(submission.email) },
            { label: 'Phone', value: orDash(submission.phone) },
            { label: 'Age group', value: orDash(ageGroupLabel(submission.age_group)) },
            { label: 'Gender', value: labelFor(reference.data, 'GENDER', submission.gender) },
            { label: 'City', value: orDash(submission.city) },
            {
              label: 'Occupation',
              // OTHER shows what they typed; every other code shows its label.
              value:
                submission.occupation === OCCUPATION.OTHER
                  ? orDash(submission.occupation_other)
                  : orDash(occupationLabel(submission.occupation)),
            },
            { label: 'Organisation', value: orDash(submission.organization) },
            // Only students carry one, and for them it is the answer staff most
            // want to see on this screen — so it is shown as a dash rather than
            // hidden, which would leave a reviewer unsure whether it was asked.
            {
              label: 'Institution',
              value: labelFor(reference.data, 'INSTITUTION', submission.institution),
            },
            // Not a fact about the person, but the one thing on this screen a
            // reviewer can act on twice over: it tells outreach which channel
            // produced this submission, and it is the first thing to go stale
            // if it is only kept on the request.
            {
              label: 'Heard about us via',
              value: labelFor(reference.data, 'REFERRAL_SOURCE', submission.referral_source),
            },
            {
              label: 'Areas of interest',
              value: labelsFor(reference.data, 'AREA_OF_INTEREST', submission.areas_of_interest),
            },
            {
              label: 'Languages',
              value: labelsFor(reference.data, 'LANGUAGE', submission.languages),
            },
            { label: 'Notes', value: orDash(submission.skills), wide: true },
          ]}
        />
      </Card>

      <Card title="Outcome">
        <DetailList
          items={[
            { label: 'Status', value: humaniseCode(submission.status) },
            { label: 'Reviewed by', value: orDash(submission.reviewed_by) },
            { label: 'Reviewed at', value: formatDateTime(submission.reviewed_at) },
            ...(submission.rejection_reason
              ? [{ label: 'Reason', value: submission.rejection_reason, wide: true }]
              : []),
          ]}
        />

        {submission.possible_duplicate_of && (
          // Read-only here too. The action — activate, or delete as a duplicate —
          // is on the volunteer.
          <p className={styles.duplicateNote}>
            Its details matched an existing volunteer.
          </p>
        )}
      </Card>
    </>
  );
}

/**
 * A stored code shown as the label staff maintain in NocoDB (Plan D5).
 *
 * Falls back to the raw code rather than to a dash: a value that no longer
 * appears in `reference_values` — retired after this person submitted the form —
 * is information, and hiding it would make the record look empty.
 */
function labelFor(
  reference: ReferenceValuesByCategory | undefined,
  category: string,
  code: string | null
): string {
  if (!code) return '—';
  const match = reference?.[category]?.find((value) => value.code === code);
  return match?.label ?? humaniseCode(code);
}

function labelsFor(
  reference: ReferenceValuesByCategory | undefined,
  category: string,
  codes: string | null
): string {
  const list = splitCodes(codes);
  if (list.length === 0) return '—';
  return list.map((code) => labelFor(reference, category, code)).join(', ');
}
